import { Check } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { PACKAGES } from "@/lib/site";

const OPTIONS = [
  {
    id: "350",
    pathway: "Pathway 01",
    name: "SAMYAK",
    subtitle: "Nadi Pariksha Consultation",
    price: "$350",
    positioning: "For parents seeking an Ayurvedic understanding of their child's individual patterns.",
    includes: [
      "In-person Nadi Pariksha / pulse assessment",
      "Vata–Pitta–Kapha analysis",
      "Current imbalances and key concerns",
      "Personalized diet guidance",
      "Lifestyle and daily routine recommendations",
      "Summary and guidance notes",
    ],
    bestFor: "Parents who want a focused Ayurvedic consultation and personalized guidance.",
    cta: "REQUEST $350 CONSULTATION",
    value: PACKAGES.p350,
    featured: false,
  },
  {
    id: "600",
    pathway: "Pathway 02",
    name: "MEDHA",
    subtitle: "Nadi Pariksha + 11 Brain Systems Assessment",
    price: "Contact Our Team for Details",
    positioning: "A deeper assessment of the child's functional patterns across 11 brain systems.",
    includes: [
      "Everything included in the Samyak pathway",
      "11 Brain Systems functional-pattern assessment",
      "Ayurvedic Brain Type explanation",
      "Detailed analysis of speech, attention, sensory processing, sleep, digestion, behavior, emotions, motivation and more",
      "Personalized diet & lifestyle plan",
      "Comprehensive assessment report",
    ],
    bestFor: "Parents looking for a deeper understanding of their child's individual functional patterns.",
    cta: "ENQUIRE ABOUT MEDHA",
    value: PACKAGES.p600,
    featured: true,
  },
  {
    id: "1000",
    pathway: "Pathway 03",
    name: "SAMANVAYA",
    subtitle: "Complete Brain & Marma Program",
    price: "Contact Our Team for Details",
    positioning: "A comprehensive pathway combining assessment with personalized Marma guidance and parent-led home support.",
    includes: [
      "Everything included in the Medha pathway",
      "Personalized Marma point selection",
      "One-to-one parent training with Dr. Santhisree",
      "Practical demonstration and hands-on practice",
      "Guidance on how to locate and gently stimulate selected points",
      "Guidance on frequency, duration and precautions",
      "Personalized home routine",
      "Detailed take-home protocol (PDF)",
    ],
    bestFor: "Parents looking for a comprehensive assessment plus a doctor-selected, child-specific Marma home-support protocol.",
    cta: "ENQUIRE ABOUT SAMANVAYA",
    value: PACKAGES.p1000,
    img: "/marma-neck.jpg",
    featured: false,
  },
];

const Card = ({ opt, onRequest, delay }) => {
  const dark = opt.featured;
  return (
    <Reveal delay={delay} className={dark ? "lg:-translate-y-5" : ""}>
      <div
        data-testid={`package-card-${opt.id}`}
        className={`relative h-full flex flex-col rounded-3xl p-7 lg:p-9 transition-all duration-500 hover:-translate-y-1.5 ${
          dark
            ? "bg-[#1B3B2B] text-[#FAF7F2] shadow-[0_28px_70px_rgb(27,59,43,0.35)] border border-[#C5A059]/50"
            : "bg-[#FAF7F2] border border-[#E5D3AD]/50 hover:border-[#C5A059] shadow-[0_8px_30px_rgb(27,59,43,0.06)]"
        }`}
      >
        {dark && (
          <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-[#C5A059] text-[#1B3B2B] text-[10px] font-bold tracking-[0.2em] uppercase px-4 py-1.5 rounded-full">
            Deeper Assessment
          </span>
        )}
        <p className={`text-[11px] font-semibold tracking-[0.24em] uppercase ${dark ? "text-[#C5A059]" : "text-[#B85D43]"}`}>
          {opt.pathway}
        </p>
        <h3 className={`mt-3 font-serif text-3xl lg:text-4xl tracking-wide ${dark ? "text-[#FAF7F2]" : "text-[#1B3B2B]"}`}>
          {opt.name}
        </h3>
        <p className={`mt-1.5 text-[13px] leading-snug ${dark ? "text-[#FAF7F2]/70" : "text-[#5C6E62]"}`}>{opt.subtitle}</p>
        <p className={`mt-5 font-serif ${opt.price.length > 8 ? "text-2xl leading-snug" : "text-5xl"} ${dark ? "text-[#C5A059]" : "text-[#1B3B2B]"}`}>{opt.price}</p>
        {opt.img && (
          <div className="mt-5 rounded-2xl overflow-hidden border border-[#E5D3AD]/50">
            <img
              src={opt.img}
              alt="Personalized Marma point guidance for children at Sree Prathama Ayurvedam"
              loading="lazy"
              className="w-full h-48 object-cover hover:scale-105 transition-transform duration-700"
              style={{ objectPosition: "center 30%" }}
            />
          </div>
        )}
        <p className={`mt-4 text-sm italic font-serif ${dark ? "text-[#FAF7F2]/85" : "text-[#5B7566]"}`}>{opt.positioning}</p>

        <ul className="mt-6 space-y-3 flex-1">
          {opt.includes.map((inc, i) => (
            <li key={i} className="flex items-start gap-3">
              <span className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${dark ? "bg-[#C5A059]/20" : "bg-[#EAF0EC]"}`}>
                <Check className={`w-3 h-3 ${dark ? "text-[#C5A059]" : "text-[#1B3B2B]"}`} />
              </span>
              <span className={`text-[13px] leading-relaxed ${dark ? "text-[#FAF7F2]/85" : "text-[#2D3A32]"}`}>{inc}</span>
            </li>
          ))}
        </ul>

        <div className={`mt-7 rounded-2xl p-4 text-[13px] leading-relaxed ${dark ? "bg-[#FAF7F2]/8 border border-[#C5A059]/25 text-[#FAF7F2]/85" : "bg-[#EAF0EC] text-[#2D3A32]"}`}>
          <span className={`font-semibold ${dark ? "text-[#C5A059]" : "text-[#1B3B2B]"}`}>Best for: </span>
          {opt.bestFor}
        </div>

        <button
          data-testid={`package-${opt.id}-cta`}
          onClick={() => onRequest(opt.value)}
          className={`mt-6 w-full font-semibold tracking-wide text-[13px] px-6 py-4 rounded-full transition-all duration-300 hover:-translate-y-0.5 ${
            dark
              ? "bg-[#C5A059] text-[#1B3B2B] hover:bg-[#D4AF37] shadow-[0_12px_32px_rgb(197,160,89,0.3)]"
              : "bg-[#1B3B2B] text-[#FAF7F2] hover:bg-[#132C20]"
          }`}
        >
          {opt.cta}
        </button>
      </div>
    </Reveal>
  );
};

export default function Packages({ onRequest }) {
  return (
    <section id="packages" data-testid="packages-section" className="bg-[#FAF7F2] py-16 lg:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal className="text-center max-w-2xl mx-auto">
          <Eyebrow>
            <span className="mx-auto">Chapter 05 · Consultation Pathways</span>
          </Eyebrow>
          <h2 className="mt-5 font-serif text-3xl sm:text-4xl lg:text-5xl text-[#1B3B2B] tracking-tight leading-tight">
            Choose the Level of Support That Fits Your Child
          </h2>
          <p className="mt-4 text-base text-[#5C6E62]">
            Three consultation pathways designed for different levels of understanding and parent guidance.
          </p>
        </Reveal>
        <div className="mt-14 lg:mt-20 grid lg:grid-cols-3 gap-6 lg:gap-7 items-start">
          {OPTIONS.map((opt, i) => (
            <Card key={opt.id} opt={opt} onRequest={onRequest} delay={i * 0.12} />
          ))}
        </div>
      </div>
    </section>
  );
}
